<template>
  <!-- ProgressBar -->
  <div
    class="player-actions d-flex align-center justify-space-between pt-1 pb-3"
  >
    <!-- Left actions -->
    <div class="w-1/3">
      <EToggleSwitch
        v-if="showPlayerModeToggle"
        v-model="recordingType"
        :options="switchOptions"
        :default-value="recordingType"
        color="primary"
        size="sm"
      />
      <div
        v-else-if="!isVideo"
        class="player-actions__text d-flex flex-column align-left white--text font-weight-medium"
      >
        <div
          v-if="$vuetify.breakpoint.mdAndUp && !isLive && !$device.isIos"
          class="subtitle-2 mr-3"
        >
          {{ infoText.index }}
        </div>
        <div v-if="!isLive" :class="$device.isIos ? 'caption' : 'subtitle-2'">
          {{ infoText.label }}
        </div>
      </div>
    </div>
    <!-- Center actions -->
    <div
      :key="isVideo"
      class="player-actions__center player-actions__play-pause d-flex justify-center align-center"
    >
      <PlayerActionButton
        v-if="!isTimeline"
        :disabled="backwardsButtonsDisabled"
        :icon-name="
          $device.isIos ? 'fas fa-backward' : 'fa-regular fa-backward-step'
        "
        tooltip-text="First Frame"
        @click="setFrameIndex(0)"
      />

      <PlayerActionButton
        v-if="!isVideo"
        :disabled="backwardsButtonsDisabled"
        :icon-name="$device.isIos ? 'fas fa-caret-left' : 'fa-angle-left'"
        tooltip-text="Previous Frame"
        @click="updateFrameIndexBy(-1)"
      />

      <PlayerActionButton
        v-if="isVideo"
        :disabled="backwardsButtonsDisabled"
        tooltip-text="Back 10s"
        @click="updateFrameIndexBy(-10)"
      >
        <ESvgIcon
          :class="frameIndex < 1 || frames.length <= 1 ? 'opacity-20' : ''"
          icon="back10"
        />
      </PlayerActionButton>
      <!-- Play / Pause -->
      <PlayPauseButton
        :show-tooltip="$vuetify.breakpoint.mdAndUp && !$device.isIos"
        :is-playing="isPlaying"
        :disabled="(disablePlayButton || isLastFrame) && !isLive && !isVideo"
        :sm="!$device.isIos && $vuetify.breakpoint.smAndUp"
        :xs="$device.isIos || $vuetify.breakpoint.xsOnly"
        @click="togglePlaying"
      />

      <PlayerActionButton
        v-if="isVideo"
        :disabled="forwardsButtonsDisabled"
        tooltip-text="Forward 10s"
        @click="updateFrameIndexBy(10)"
      >
        <ESvgIcon :class="isLastFrame ? 'opacity-20' : ''" icon="forward10" />
      </PlayerActionButton>

      <PlayerActionButton
        v-if="!isVideo"
        :disabled="forwardsButtonsDisabled"
        :icon-name="$device.isIos ? 'fas fa-caret-right' : 'fa-angle-right'"
        tooltip-text="Next Frame"
        @click="updateFrameIndexBy(1)"
      />

      <PlayerActionButton
        v-if="!isTimeline"
        :disabled="forwardsButtonsDisabled"
        :icon-name="
          $device.isIos ? 'fas fa-forward' : 'fa-regular fa-forward-step'
        "
        tooltip-text="Last Frame"
        @click="setFrameIndex(frames.length - 1)"
      />
    </div>

    <!-- Right actions -->
    <div class="d-flex align-center justify-end w-1/3">
      <div
        v-if="hasLive && isLive"
        class="player-live-indicator mr-4 player-actions__text white--text font-weight-medium caption player-live-indicator--active d-flex align-center"
      >
        <span class="mr-1">
          {{ $t("content.live") }}
        </span>
        <v-badge
          offset-x="0"
          offset-y="-10"
          dot
          size="20"
          class="badge--pulsating badge--bright"
        />
      </div>
      <v-btn
        v-else-if="hasLive"
        x-small
        class="mr-2"
        :class="{ 'pa-2': buttonXSmall }"
        @click="enableLive"
      >
        <span>
          {{ $t("content.live") }}
        </span>
      </v-btn>
      <!-- Playback speed -->
      <div
        class="caption white--text position-relative"
        :class="
          $vuetify.breakpoint.mdAndUp && !$device.isIos ? 'mr-5' : 'r-1 px-1'
        "
      >
        <v-menu
          :nudge-top="30"
          content-class="recordings__player__speed"
          top
          origin="top left"
          dark
          rounded
          min-width="100"
          attach
          :close-on-content-click="false"
          :disabled="isLive"
        >
          <template #activator="{ on, attrs }">
            <button
              v-if="$vuetify.breakpoint.mdAndUp && !$device.isIos"
              class="player-actions__text subtitle-2 white--text font-weight-medium"
              :class="{
                'opacity-50 cursor-default': isLive,
              }"
              v-bind="attrs"
              v-on="on"
            >
              {{ $t("content.recordings.playback_speed") }}:
              <span class="recordings__player__speed--text">
                × {{ allowedSpeeds[selectedSpeedIndex] }}
              </span>
            </button>
            <button v-else :disabled="isLive" small v-bind="attrs" v-on="on">
              <v-icon>fas fa-tachometer-alt </v-icon>
            </button>
          </template>
          <v-card
            class="custom-slider overflow-y-hidden"
            color="#101c27CC"
            elevation="0"
          >
            <v-slider
              v-model="selectedSpeedIndex"
              color="#eee"
              track-color="grey"
              class="py-4"
              height="150"
              :tick-labels="allowedSpeeds"
              :max="allowedSpeeds.length - 1"
              step="1"
              ticks="always"
              tick-size="2"
              vertical
              @change="updateTimePerFrame"
            >
              <template #label="{}"> </template>
            </v-slider>
          </v-card>
        </v-menu>
      </div>

      <!-- Quality -->
      <div
        v-if="showSnapshotQuality"
        class="caption white--text position-relative"
        :class="
          $vuetify.breakpoint.mdAndUp && !$device.isIos ? 'mr-4' : 'r-1 px-1'
        "
      >
        <v-menu
          :nudge-top="30"
          content-class="recordings__player__quality"
          top
          origin="top left"
          dark
          rounded
          min-width="100"
          attach
          :close-on-content-click="false"
        >
          <template #activator="{ on, attrs }">
            <button
              v-if="$vuetify.breakpoint.mdAndUp && !$device.isIos"
              class="player-actions__text subtitle-2 white--text font-weight-medium"
              :class="{
                'opacity-50 cursor-default': isLive,
              }"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon dark v-bind="attrs" color="white" v-on="on">
                fas fa-gear
              </v-icon>

              <span class="recordings__player__quality--text">
                ({{ allowedQualities[selectedQualityIndex] }})
              </span>
            </button>
            <button
              v-else
              :disabled="isLive"
              dark
              v-bind="attrs"
              small
              v-on="on"
            >
              <v-icon>fas fa-filter </v-icon>
            </button>
          </template>
          <v-card
            class="custom-slider overflow-y-hidden"
            color="#101c27CC"
            elevation="0"
          >
            <v-slider
              v-model="selectedQualityIndex"
              color="#eee"
              track-color="grey"
              class="py-4"
              height="150"
              :tick-labels="allowedQualities"
              :max="allowedQualities.length - 1"
              step="1"
              ticks="always"
              tick-size="4"
              vertical
              @change="updateQuality"
            >
              <template #label="{}"> </template>
            </v-slider>
          </v-card>
        </v-menu>
      </div>

      <div>
        <!-- Fullscreen -->
        <v-tooltip
          :disabled="!$vuetify.breakpoint.mdAndUp || $device.isIos"
          top
          nudge-top="15"
          nudge-left="35"
          transition="none"
          color="#000"
        >
          <template #activator="{ on }">
            <div
              class="player-actions__btn mr-2"
              data-test-id="recordings-fullscreen"
              v-on="on"
              @click="toggleFullscreen"
            >
              <v-icon :small="$device.isIos" dark>
                {{ $device.isIos ? "fas fa-expand" : "fas fa-expand" }}
              </v-icon>
            </div>
          </template>
          <span class="player-actions__text">
            {{ $t("actions.fullscreen") }}
          </span>
        </v-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import { AnalyticsEvent, ImageQuality, PlayerMode } from "@evercam/shared/types"
import PlayPauseButton from "@evercam/shared/components/imagePlayer/PlayPauseButton"
import { exitFullScreen, makeFullScreen } from "@evercam/ui"
import PlayerActionButton from "@evercam/shared/components/imagePlayer/PlayerActionButton.vue"

export default {
  name: "PlayerActions",
  components: {
    PlayPauseButton,
    PlayerActionButton,
  },
  props: {
    frames: {
      type: Array,
      default: () => [],
    },
    frameIndex: {
      type: Number,
      default: 0,
    },
    isLastFrame: {
      type: Boolean,
      default: true,
    },
    isFetching: {
      type: Boolean,
      default: false,
    },
    timePerFrame: {
      type: Number,
      default: 250,
    },
    isPlaying: {
      type: Boolean,
      default: false,
    },
    infoText: {
      type: Object,
      default: () => ({
        index: "",
        label: "",
      }),
    },
    hasLive: {
      type: Boolean,
      default: false,
    },
    isLive: {
      type: Boolean,
      default: false,
    },
    disablePlayButton: {
      type: Boolean,
      default: false,
    },
    initialSnapshotQuality: {
      type: [String, Number],
      default: ImageQuality.Auto,
    },
    showSnapshotQuality: {
      type: Boolean,
      default: false,
    },
    isVideo: {
      type: Boolean,
      default: false,
    },
    showPlayerModeToggle: {
      type: Boolean,
      default: false,
    },
    shouldSavePlayerOptions: {
      type: Boolean,
      default: false,
    },
    isTimeline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      recordingType: "jpegs",
      allowedSpeeds: [0.25, 0.5, 0.75, 1, 1.5, 2, 2.5],
      selectedSpeedIndex: 3,
      allowedQualities: Object.values(ImageQuality),
      selectedQualityIndex: 4,
      isFullscreen: false,
    }
  },
  computed: {
    backwardsButtonsDisabled() {
      return this.frames.length <= 1 || this.isFetching || this.frameIndex < 1
    },
    forwardsButtonsDisabled() {
      return (
        this.isLive ||
        this.isLastFrame ||
        this.frames.length <= 1 ||
        this.isFetching
      )
    },
    buttonXSmall() {
      return this.$vuetify.breakpoint.xsOnly || this.$device.isIos
    },
    switchOptions() {
      return [
        {
          label: "VIDEO",
          value: PlayerMode.Video,
        },
        {
          label: "JPEGS",
          value: PlayerMode.Jpegs,
        },
      ]
    },
  },
  watch: {
    isVideo: {
      handler(value) {
        this.recordingType = value ? PlayerMode.Video : PlayerMode.Jpegs
      },
      immediate: true,
    },
    recordingType(value, oldValue) {
      if (value !== oldValue) {
        this.$root.$emit("analytics-event", {
          eventId: AnalyticsEvent.PlayerSwitchMode,
          params: { mode: value },
        })
      }
      this.$emit("update-player-mode", value)
    },
  },
  mounted() {
    this.initPlayerSettings()
  },
  methods: {
    initPlayerSettings() {
      if (!this.shouldSavePlayerOptions) {
        return
      }
      const speed = window.localStorage.getItem("snapshotsPlayerSpeed")
      const quality = window.localStorage.getItem("snapshotsPlayerQuality")
      this.selectedQualityIndex = this.allowedQualities.indexOf(
        quality ?? this.initialSnapshotQuality
      )
      this.selectedSpeedIndex = speed
        ? this.allowedSpeeds.indexOf(Number.parseFloat(speed))
        : 3
      this.updateTimePerFrame()
      this.updateQuality()
    },
    setFrameIndex(newIndex) {
      const amount = newIndex - this.frameIndex
      if (newIndex > this.frameIndex) {
        this.$root.$emit("analytics-event", {
          eventId: AnalyticsEvent.PlayerJumptoNextTicks,
          params: { ticks: amount },
        })
      } else {
        this.$root.$emit("analytics-event", {
          eventId: AnalyticsEvent.PlayerJumptoPreviousTicks,
          params: { ticks: amount },
        })
      }
      if (newIndex >= this.frames.length) {
        newIndex = this.frames.length - 1
      }
      if (newIndex < 0) {
        newIndex = 0
      }
      if (newIndex >= 0) {
        this.$emit("update-frame-index", newIndex)
      }
    },

    updateFrameIndexBy(amount) {
      let newIndex = this.frameIndex + amount
      this.setFrameIndex(newIndex)
    },
    updateTimePerFrame() {
      this.$root.$emit("analytics-event", {
        eventId: AnalyticsEvent.PlayerSelectSpeed,
        params: { speed: this.allowedSpeeds[this.selectedSpeedIndex] },
      })
      const timePerFrame =
        this.timePerFrame / this.allowedSpeeds[this.selectedSpeedIndex]
      this.$emit("update-time-per-frame", timePerFrame)
      if (this.shouldSavePlayerOptions) {
        window.localStorage.setItem(
          "snapshotsPlayerSpeed",
          this.allowedSpeeds[this.selectedSpeedIndex]
        )
      }
    },
    updateQuality() {
      this.$root.$emit("analytics-event", {
        eventId: AnalyticsEvent.PlayerSelectQuality,
        params: { quality: this.allowedQualities[this.selectedQualityIndex] },
      })
      this.$emit(
        "snapshots-quality",
        this.allowedQualities[this.selectedQualityIndex]
      )
      if (this.shouldSavePlayerOptions) {
        window.localStorage.setItem(
          "snapshotsPlayerQuality",
          this.allowedQualities[this.selectedQualityIndex]
        )
      }
    },
    toggleFullscreen() {
      this.$root.$emit("analytics-event", {
        eventId: AnalyticsEvent.PlayerToggleFullscreen,
        params: { active: !this.isFullscreen },
      })
      if (this.$device.isIos) {
        this.$emit("toggle-ios-fullscreen")

        return
      }
      if (!this.isFullscreen) {
        makeFullScreen(this.$globalRefs.playerWrapper).then(() => {
          this.isFullscreen = true
        })
      } else {
        exitFullScreen().then(() => {
          this.isFullscreen = false
        })
      }
      this.$root.$emit("image-player-fullscreen", this.isFullscreen)
      this.$emit("update-fullscreen", !this.isFullscreen)
    },
    togglePlaying() {
      if (this.isLive) {
        this.$emit("update-live", false)
      }
      this.$emit("update-playback", !this.isPlaying)
      this.$root.$emit("analytics-event", {
        eventId: AnalyticsEvent.PlayerTogglePlayback,
        params: { active: !this.isPlaying },
      })
    },
    enableLive() {
      this.$emit("update-live", true)
      this.$root.$emit("analytics-event", {
        eventId: AnalyticsEvent.PlayerEnableLive,
      })
    },
  },
}
</script>

<style lang="scss">
$btn-size: 12px;

.w-1\/3 {
  width: 33.333333%;
}
.player-actions {
  position: relative;
  .theme--light.v-icon {
    color: #fff;
  }
  .theme--light.v-btn.v-btn--disabled .v-icon {
    color: rgba(255, 255, 255, 0.3) !important;
  }
  &__btn {
    cursor: pointer;
    transition: 0.2s;
    filter: drop-shadow(0 0 5px #0009);
    user-select: none;
    &:hover {
      filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.67));
    }
  }
  .video-switch label {
    font-weight: 500 !important;
    color: white !important;
    font-size: 0.875rem !important;
  }
  .video-switch label,
  &__text {
    text-shadow: 0 0 4px rgb(0 0 0 / 75%);
    user-select: none;
    .v-icon.v-icon--link {
      color: white !important;
    }
  }
  &__player-mode--mobile .v-input--radio-group__input {
    display: flex !important;
    flex-direction: column !important;
  }

  &__tooltip {
    opacity: 0;
    top: -50px;
    position: absolute;
    filter: none;
    background: #333c;
  }
  .v-slider {
    &__track-container,
    &__ticks-container {
      cursor: pointer !important;
      & > {
        cursor: pointer !important;
      }
    }
    &__tick-label {
      font-size: 12px !important;
      color: var(--v-on_background-base) !important;
    }
  }
  .v-radio .mdi-radiobox-blank {
    color: white;
  }
}

.recordings__player__speed,
.recordings__player__quality {
  right: 0;
  left: auto !important;
}

.player-live-indicator {
  $livEColor--active: #f00;
  color: #ddd;
  &--active {
    .badge--pulsating .v-badge__badge {
      background-color: $livEColor--active !important;
      border-color: $livEColor--active !important;
      height: 7px;
      width: 7px;
      margin-top: 6px;
      &:before,
      &:after {
        background-color: $livEColor--active;
        width: 16px;
        height: 16px;
        border-radius: 8px;
        left: -4px;
      }
    }
  }
}
.player-live-indicator {
  background: #111827;
  padding: 2px;
  padding-left: 11px;
  border-radius: 5px;
  padding-right: 16px;
  filter: drop-shadow(0px 0px 6px #111827);
}
</style>
